// M15 screens: the AI settings (provider, key kept only in this browser, models, Remove key) and the
// side-by-side view of an AI image result, always labelled AI-generated, with the delivery-app note.
import { AlertTriangle, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useT } from '../../i18n';
import {
  DEFAULT_MODELS,
  loadAi,
  maskKey,
  removeAiKey,
  saveAi,
  useAi,
  type AiProvider,
  type AiSettings,
} from '../../features/ai/aiSettings';
import { aiImageActive, saveAiResult, smartFixActive, useAiUi } from '../../features/ai/aiActions';
import s from './Dialog.module.css';

export function AiSettingsDialog() {
  const t = useT();
  const open = useAiUi((a) => a.settingsOpen);
  const saved = useAi((a) => a.settings);
  const [provider, setProvider] = useState<AiProvider>('openai');
  const [key, setKey] = useState('');
  const [models, setModels] = useState<AiSettings['models']>(DEFAULT_MODELS);
  useEffect(() => {
    if (!open) return;
    void loadAi().then((cur) => {
      setProvider(cur?.provider ?? 'openai');
      setModels(cur?.models ?? DEFAULT_MODELS);
      setKey('');
    });
  }, [open]);
  if (!open) return null;
  const close = () => useAiUi.setState({ settingsOpen: false });
  const m = models[provider];
  return (
    <div
      className={s.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={t('aiSettings')}
      data-testid="ai-settings"
    >
      <div className={s.dialog} style={{ maxWidth: 540 }}>
        <h2>{t('aiSettings')}</h2>
        <p className={s.muted}>{t('aiIntro')}</p>
        <div role="radiogroup" style={{ display: 'flex', gap: 12, margin: '8px 0' }}>
          {(['openai', 'google'] as const).map((p) => (
            <label key={p}>
              <input
                type="radio"
                name="ai-provider"
                data-testid={`ai-provider-${p}`}
                checked={provider === p}
                onChange={() => setProvider(p)}
              />{' '}
              {p === 'openai' ? 'OpenAI' : 'Google (Gemini)'}
            </label>
          ))}
        </div>
        <label style={{ display: 'block', margin: '6px 0' }}>
          {t('aiKey')}
          <input
            type="password"
            autoComplete="off"
            data-testid="ai-key"
            value={key}
            placeholder={
              saved?.key && saved.provider === provider ? maskKey(saved.key) : t('aiKeyPlaceholder')
            }
            onChange={(e) => setKey(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, padding: 6 }}
          />
        </label>
        <p className={s.muted}>{t('aiKeyStored')}</p>
        <details>
          <summary>{t('aiModels')}</summary>
          <label style={{ display: 'block' }}>
            {t('aiModelVision')}{' '}
            <input
              type="text"
              value={m.vision}
              onChange={(e) => setModels({ ...models, [provider]: { ...m, vision: e.target.value } })}
            />
          </label>
          <label style={{ display: 'block' }}>
            {t('aiModelImage')}{' '}
            <input
              type="text"
              value={m.image}
              onChange={(e) => setModels({ ...models, [provider]: { ...m, image: e.target.value } })}
            />
          </label>
        </details>
        <p className={s.muted}>{t('aiPrivacy')}</p>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button
            data-testid="ai-save"
            disabled={!key.trim() && !(saved?.key && saved.provider === provider)}
            onClick={async () => {
              await saveAi({ provider, key: key.trim() || saved!.key, models });
              close();
            }}
          >
            {t('aiSave')}
          </button>
          {saved?.key && (
            <button
              data-testid="ai-remove-key"
              onClick={async () => {
                await removeAiKey();
                close();
              }}
            >
              <Trash2 className="icon" aria-hidden="true" /> {t('aiRemoveKey')}
            </button>
          )}
          <button onClick={close}>{t('cancel')}</button>
        </div>
        <p className={s.muted} data-testid="ai-key-state">
          {saved?.key
            ? t('aiKeySet', {
                provider: saved.provider === 'openai' ? 'OpenAI' : 'Google',
                key: maskKey(saved.key),
              })
            : t('aiNoKey')}
        </p>
      </div>
    </div>
  );
}

export function AiCompareDialog() {
  const t = useT();
  const c = useAiUi((a) => a.compare);
  const [msg, setMsg] = useState<string | null>(null);
  if (!c) return null;
  const close = () => {
    URL.revokeObjectURL(c.original);
    URL.revokeObjectURL(c.result);
    useAiUi.setState({ compare: null });
    setMsg(null);
  };
  const fig = (src: string, label: string, testId: string, warn = false) => (
    <figure style={{ margin: 0, flex: 1, minWidth: 0 }}>
      <img
        src={src}
        alt={label}
        data-testid={testId}
        style={{
          width: '100%',
          maxHeight: '58vh',
          objectFit: 'contain',
          background: 'var(--photo-surround)',
        }}
      />
      <figcaption style={{ fontWeight: 500, marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
        {warn && <AlertTriangle className="icon" aria-hidden="true" />}
        {label}
      </figcaption>
    </figure>
  );
  return (
    <div
      className={s.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={t('aiCompareTitle')}
      data-testid="ai-compare"
    >
      <div className={s.dialog} style={{ maxWidth: 1100 }}>
        <h2>{t(c.kind === 'touchup' ? 'aiTouchUp' : 'aiUpscale')}</h2>
        <div style={{ display: 'flex', gap: 12 }}>
          {fig(c.original, t('aiOriginal'), 'ai-original')}
          {fig(c.result, `${t('aiGenerated')} · ${c.width} × ${c.height}`, 'ai-result', true)}
        </div>
        <p style={{ color: 'var(--warn)', fontSize: 13 }}>{t('aiDeliveryNote')}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            data-testid="ai-save-result"
            onClick={async () => setMsg(t('saved', { name: (await saveAiResult()) ?? '' }))}
          >
            {t('aiSaveCopy')}
          </button>
          <button onClick={close}>{t('close')}</button>
        </div>
        {msg && <p role="status">{msg}</p>}
      </div>
    </div>
  );
}

/** The AI buttons for the active photo (Simple step 2 and the Pro "AI" panel); hidden without a key. */
export function AiPhotoButtons({ big = true }: { big?: boolean }) {
  const t = useT();
  const ready = useAi((a) => !!a.settings?.key);
  const busy = useAiUi((a) => a.busy);
  const error = useAiUi((a) => a.error);
  useEffect(() => void loadAi(), []);
  if (!ready) return null;
  const st = { minHeight: big ? 44 : 30, width: '100%', marginTop: 6 };
  const run = (k: 'smartFix' | 'touchup' | 'upscale') =>
    void (k === 'smartFix' ? smartFixActive() : aiImageActive(k));
  return (
    <div data-testid="ai-photo-buttons">
      <button style={st} data-testid="ai-smart-fix" disabled={!!busy} onClick={() => run('smartFix')}>
        <Sparkles className="icon" aria-hidden="true" /> {t('aiSmartFix')}
        {busy === 'smartFix' ? ' …' : ''}
      </button>
      <div style={{ display: 'flex', gap: 6 }}>
        <button style={st} data-testid="ai-touchup" disabled={!!busy} onClick={() => run('touchup')}>
          {t('aiTouchUp')}
          {busy === 'touchup' ? ' …' : ''}
        </button>
        <button style={st} data-testid="ai-upscale" disabled={!!busy} onClick={() => run('upscale')}>
          {t('aiUpscale')}
          {busy === 'upscale' ? ' …' : ''}
        </button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{t('aiSendsPhoto')}</div>
      {error && (
        <div
          role="alert"
          style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}
          data-testid="ai-error"
        >
          {t('aiFailed', { reason: error })}
        </div>
      )}
    </div>
  );
}
